import { useId, useMemo } from "react";

import type { CoaRole, CoaSketch as Sketch, Phase, WargameContact } from "../../api/types";
import { motion } from "../../components/presence";
import { citedUnit } from "../../i18n/designation";
import { t, tServer } from "../../i18n/i18n";
import {
  FriendlyUnits,
  pathOf as path,
  projection,
  SKETCH_BG,
  SKETCH_WIDTH as WIDTH,
  SketchGroundLayers,
  SketchMarks,
  unitBoxes,
  useSymbolIcons,
} from "../sketch/SketchBase";
import { SEVERITY_COLOR } from "./severity";

/** Croquis d'un mode d'action ennemi : fuseau et lignes, routes, cours d'eau, localités, amis en T1, et zone de
 *  chaque échelon ennemi au temps choisi — ou, pour « Tous », la trajectoire de chaque échelon de T1 à T3.
 *  Nord en haut, comme la carte ; même emprise pour ME1 et ME2, pour les comparer. */

export type SketchPhase = Phase | "all";

/** Ennemi : rouge OTAN hors carte (jeton `app6-hostile`). */
const HOSTILE = "var(--color-app6-hostile)";
const PHASES: Phase[] = ["T1", "T2", "T3"];

/** Trait de chaque échelon (légende et croquis). */
export const ROLE_DASH: Record<string, string | undefined> = {
  ENI_RECON_111_112_113: "5 3",
  ENI_FIRST_ECHELON_NIVEAU_INITIAL: undefined,
  ENI_SECOND_ECHELON_NIVEAU_ULT: "9 4",
  ENI_FIRES_APPUIS: "1.5 3",
};
const ROLE_FILL: Record<string, number> = {
  ENI_RECON_111_112_113: 0.08,
  ENI_FIRST_ECHELON_NIVEAU_INITIAL: 0.16,
  ENI_SECOND_ECHELON_NIVEAU_ULT: 0.12,
  ENI_FIRES_APPUIS: 0.06,
};
const CONTACT_COLOR = SEVERITY_COLOR;

interface Mark {
  x: number;
  y: number;
  label: string;
  phases: Phase[];
}

/** Trajectoire d'un échelon pour « Tous » : sur son couloir (décalé perpendiculairement à l'axe, pour que les
 *  échelons ne se recouvrent pas), repères de temps confondus s'ils se touchent, écartés s'ils se frôlent. */
function trail(points: { x: number; y: number; phase: Phase }[], offset: { x: number; y: number }): Mark[] {
  const marks: Mark[] = [];
  for (const pt of points) {
    const x = pt.x + offset.x;
    const y = pt.y + offset.y;
    const last = marks[marks.length - 1];
    if (last && Math.hypot(x - last.x, y - last.y) < 4) {
      last.phases.push(pt.phase);
      last.label = `${last.label}-${pt.phase.slice(1)}`;
      continue;
    }
    marks.push({ x, y, label: pt.phase.slice(1), phases: [pt.phase] });
  }
  for (let i = 1; i < marks.length; i += 1) {
    const a = marks[i - 1]!;
    const b = marks[i]!;
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const min = 15;
    if (d < min) {
      b.x = a.x + ((b.x - a.x) / d) * min;
      b.y = a.y + ((b.y - a.y) / d) * min;
    }
  }
  return marks;
}

/** Étiquettes des échelons sans chevauchement : décalées vers le bas tant qu'elles en recouvrent une autre. */
function placeChips(items: { key: string; x: number; y: number; text: string }[]) {
  const placed: { key: string; x: number; y: number; w: number; text: string }[] = [];
  for (const item of items) {
    const w = item.text.length * 6.4 + 12;
    let y = item.y;
    for (let i = 0; i < 6; i += 1) {
      const hit = placed.some((o) => Math.abs(o.x - item.x) < (o.w + w) / 2 && Math.abs(o.y - y) < 17);
      if (!hit) break;
      y += 18;
    }
    placed.push({ key: item.key, x: item.x, y, w, text: item.text });
  }
  return placed;
}

export function CoaSketch({
  sketch,
  roles,
  phase,
  label,
  contacts,
}: {
  sketch: Sketch;
  roles: CoaRole[];
  phase: SketchPhase;
  label: string;
  /** Contacts du wargame au temps affiché, tracés de l'unité amie à la zone ennemie. */
  contacts?: WargameContact[];
}) {
  const uid = useId().replace(/:/g, "");
  const p = useMemo(() => projection(sketch.bbox), [sketch.bbox]);
  const height = p.height;
  const icons = useSymbolIcons(sketch.ami.map((u) => u.sidc));

  const axis = {
    dx: p.x(sketch.axis.end[0]) - p.x(sketch.axis.start[0]),
    dy: p.y(sketch.axis.end[1]) - p.y(sketch.axis.start[1]),
  };
  const axisAngle = (Math.atan2(axis.dy, axis.dx) * 180) / Math.PI;
  const axisLength = Math.hypot(axis.dx, axis.dy) || 1;
  const along = { x: axis.dx / axisLength, y: axis.dy / axisLength };
  const across = { x: -along.y, y: along.x };
  const lanes = roles.map((r, i) => {
    const shift = (i - (roles.length - 1) / 2) * 16;
    const points = PHASES.flatMap((ph) => {
      const t = r.times[ph];
      return t ? [{ x: p.x(t.center[0]), y: p.y(t.center[1]), phase: ph }] : [];
    });
    return { role: r, marks: trail(points, { x: across.x * shift, y: across.y * shift }) };
  });

  const chips = placeChips(
    phase === "all"
      ? lanes
          .filter((l) => l.marks.length > 0)
          .map(({ role, marks }) => {
            const first = marks[0]!;
            const next = marks[1];
            const d = next ? Math.hypot(next.x - first.x, next.y - first.y) : 0;
            const dir = next && d > 0 ? { x: (next.x - first.x) / d, y: (next.y - first.y) / d } : along;
            const short = tServer(role.short);
            const back = short.length * 3.2 + 20;
            return { key: role.role, x: first.x - dir.x * back, y: first.y - dir.y * back, text: short };
          })
      : roles
          .map((r) => {
            const t = r.times[phase];
            return t ? { key: r.role, x: p.x(t.center[0]), y: p.y(t.center[1]), text: tServer(r.short) } : null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null),
  );

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height.toFixed(1)}`}
      className="block h-auto w-full"
      style={{ letterSpacing: "normal", fontStretch: "normal", background: SKETCH_BG }}
      role="img"
      aria-label={label}
    >
      <defs>
        <marker id={`${uid}-arrow`} viewBox="0 0 8 8" refX="6.5" refY="4" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M0 0.5 L8 4 L0 7.5 Z" fill={HOSTILE} />
        </marker>
      </defs>

      <SketchGroundLayers p={p} ground={sketch} obstacles={unitBoxes(p, sketch.ami, icons)} />
      <FriendlyUnits p={p} units={sketch.ami} icons={icons} suffix="T1" />

      {/* Échelons ennemis : zones du temps choisi, ou trajectoires de T1 à T3. */}
      {phase === "all"
        ? lanes.map(({ role, marks }) => (
            <g key={role.role}>
              {marks.length > 1 && (
                <path
                  d={marks.map((m, i) => `${i ? "L" : "M"}${m.x.toFixed(1)} ${m.y.toFixed(1)}`).join("")}
                  fill="none"
                  stroke={HOSTILE}
                  strokeWidth={1.8}
                  strokeDasharray={ROLE_DASH[role.role]}
                  markerEnd={`url(#${uid}-arrow)`}
                />
              )}
              {marks.map((m) => {
                const w = Math.max(13, m.label.length * 5 + 7);
                const postures = m.phases
                  .map((ph) => t("{phase} : {posture}", { phase: ph, posture: tServer(role.times[ph]?.posture ?? "").toLowerCase() }))
                  .join(", ");
                return (
                  <g key={m.label}>
                    <rect x={m.x - w / 2} y={m.y - 6.5} width={w} height={13} rx={6.5} fill="var(--color-surface)" stroke={HOSTILE} strokeWidth={1.3} />
                    <text x={m.x} y={m.y + 3.2} textAnchor="middle" fontSize={8.5} fontWeight={600} fill={HOSTILE}>
                      {m.label}
                    </text>
                    <title>{`${tServer(role.label)} · ${postures}`}</title>
                  </g>
                );
              })}
            </g>
          ))
        : roles.map((r) => {
            const time = r.times[phase];
            if (!time) return null;
            return (
              <path
                key={r.role}
                d={path(p, time.ring, true)}
                fill={HOSTILE}
                fillOpacity={ROLE_FILL[r.role] ?? 0.1}
                stroke={HOSTILE}
                strokeWidth={1.5}
                strokeDasharray={ROLE_DASH[r.role]}
                strokeLinejoin="round"
              >
                <title>{`${tServer(r.label)} · ${phase} · ${tServer(time.posture)}${r.units.length ? ` · ${r.units.map(citedUnit).join(", ")}` : ""}`}</title>
              </path>
            );
          })}
      {contacts?.map((c) => {
        const ax = p.x(c.ami_position[0]);
        const ay = p.y(c.ami_position[1]);
        const color = CONTACT_COLOR[c.severity];
        return (
          <g key={`${c.ami}-${c.eni_role}`} className={motion("fade")}>
            {c.distance_m > 0 && (
              <line x1={ax} y1={ay} x2={p.x(c.eni_point[0])} y2={p.y(c.eni_point[1])} stroke={color} strokeWidth={1.8}
                strokeDasharray="4 3" strokeLinecap="round" />
            )}
            <circle cx={ax} cy={ay} r={c.distance_m > 0 ? 3.5 : 9} fill={c.distance_m > 0 ? color : "none"} stroke={color}
              strokeWidth={1.8} />
            <title>{t("{ami} — {eni} : {severity}", { ami: c.ami, eni: tServer(c.eni_label), severity: tServer(c.severity_label).toLowerCase() })}</title>
          </g>
        );
      })}
      {chips.map((c) => (
        <g key={c.key} transform={`translate(${Math.min(Math.max(c.x, c.w / 2 + 4), WIDTH - c.w / 2 - 4)} ${c.y})`}>
          <rect x={-c.w / 2} y={-8.5} width={c.w} height={17} rx={8.5} fill="var(--color-surface)" stroke={HOSTILE} strokeWidth={1.2} />
          <text y={3.6} textAnchor="middle" fontSize={10} fontWeight={600} fill={HOSTILE}>
            {c.text}
          </text>
        </g>
      ))}

      <SketchMarks p={p} axisAngle={axisAngle} />
    </svg>
  );
}
